import { update } from 'state/util';
import { STATUS } from 'state/requestStatus/reducer';
import { parseCandidatePageId } from 'state/ballots/position';

export function makeVotePrefKey(pageId, userId) {
  return `${userId}#${pageId}`;
}

/**
 * Storage for personal vote preferences & relaying status messages
 * related to the current operation.
 *
 * votePrefs state sub-tree:
 * {
 *   "candidate-page-id#userId": {
 *     data: {
 *       vote: "Y", // Y || N, null or missing means undecided
 *     },
 *     message: null || "some status/error message",
 *     status: STATUS.COMPLETE
 *   },
 *   ... other page preferences ...,
 * }
 *
 * Action Dispatch Structure:
 *  {
 *    type: "[WRITE|GET]_VOTE_PREF_REQUESTED",
 *    payload: {
 *      pageId: "example-candidate-page",
 *      userId: "my userId",
 *      data: {
 *        vote: "Y",
 *      }
 *    }
 *  }
 */

// we add page keys to this as we add preferences
const initialState = {};

export const votePrefsReducer = (state, action) => {
  if (state === undefined)
    return initialState;

  if (action.type === "LOGOUT_REQUESTED")
      return initialState;

  else if (action.type === "BULK_VOTE_PREFS_SUCCESSFUL") {
    const votePrefs = {};
    action.payload.forEach(pref => {
      const pageId = pref.pageId;
      const userId = pref.userId;
      const key = makeVotePrefKey(pageId, userId);
      votePrefs[key] = pref;
    });
    return update(state, votePrefs);
  }

  let key = null;
  let currentData = null;
  if (action.payload && action.payload.pageId && action.payload.userId) {
    key = makeVotePrefKey(action.payload.pageId, action.payload.userId);
    currentData = state[key] ? state[key].data : {};
  }

  switch(action.type) {
    // using "WRITE" because create/update are same
    case "WRITE_VOTE_PREF_REQUESTED":
      return update(state, {
        [key]: {
          message: "Saving...",
          status: STATUS.SAVING,
        },
      });

    // TODO: if candidate/position page, parse the position
    // data and update the relevant votePref pages (if they exist)
    // Also do the same for GET_VOTE_PREF_SUCCESSFUL
    case "WRITE_VOTE_PREF_SUCCESSFUL":
      const updatedVPs = {
        [key]: {
          data: action.payload.data,
          user: action.payload.user,
          message: "Save successful",
          status: STATUS.COMPLETE,
        },
      };
      const newVP = action.payload;
      // for checking/updating candidate vote prefs
      const newCandPos = parseCandidatePageId(newVP.pageId);
      Object.keys(state).forEach((thisKey) => {
        // skip the already to-be updated vote pref
        if (thisKey === key) {
          return;
        }
        const [thisUID, thisPID ] = thisKey.split("#");
        if (!thisUID || !thisPID) {
          return;
        }
        const vp = state[thisKey];
        // if this is a prop or a prop was saved, we just return
        // the existing data from the state tree. we only want to update
        // state tree candidate pages if we just saved a candidate
        const thisCandPos = parseCandidatePageId(thisPID);
        if (!newCandPos || !thisCandPos) {
          return;
        }
        const thisPosition = thisCandPos[1];
        const thisCandidate = thisCandPos[2];
        const newPosition = newCandPos[1];
        // skip non-saved positions, we only update the other position
        // vote prefs matching the one saved
        if (thisPosition !== newPosition) {
          return;
        }
        // we have a non-yes position to update for this vote pref
        if (newVP.position[thisCandidate]) {
          updatedVPs[thisKey] = Object.assign({}, vp, {
            data: { vote: newVP.position[thisCandidate] }
          });
        }
        // we know some other candidate for this position is "yes"
        // so that means this has to be no, now
        else if (newVP.position.yes !== thisCandidate) {
          updatedVPs[thisKey] =  Object.assign({}, vp, {
            data: { vote: "N" }
          });
        }
      });
      return update(state, updatedVPs);

    case "WRITE_VOTE_PREF_FAILED":
      return update(state, {
        [key]: {
          message: action.payload.message,
          status: STATUS.FAILED,
        },
      });

    case "GET_VOTE_PREF_REQUESTED":
      return update(state, {
        [key]: {
          data: currentData || {},
          message: "Loading page...",
          status: STATUS.LOADING,
        },
      });

    case "GET_VOTE_PREF_SUCCESSFUL":
      return update(state, {
        [key]: {
          data: action.payload.data,
          user: action.payload.user,
          message: null,
          status: STATUS.COMPLETE,
        },
      });

    case "GET_VOTE_PREF_FAILED":
      if (action.payload.statusCode === 404) {
        // return a blank vote pref for creating if not found
        return update(state, {
          [key]: {
            message: null,
            data: {},
            status: STATUS.NOT_FOUND,
          },
        }, true);
      }
      return update(state, {
        [key]: {
          message: action.payload.message,
          data: {},
          status: STATUS.FAILED,
        },
      }, true);

    default:
      return state;
  }
};




// WEBPACK FOOTER //
// ./src/state/votePrefs/reducer.js